import React from "react";
import { TrendingUp, Users, AlertTriangle, FileText } from "lucide-react";
import PercentileRank from "../../../assets/percentile-rank.svg";

interface ScoreCardProps {
  title: string;
  value: string;
  subtitle: string;
  type: "resume" | "score" | "percentile" | "alert";
  progress?: number;
  color?: "blue" | "green" | "red" | "yellow" | "teal" | "lightblue";
  rightSideValue?: string;
}

const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  value,
  subtitle,
  type,
  progress = 0,
  color = "blue",
  rightSideValue,
}) => {
  const getIcon = () => {
    switch (type) {
      case "resume":
        return <FileText className="w-4 h-4" />;
      case "score":
        return <TrendingUp className="w-4 h-4" />;
      case "percentile":
        return <img src={PercentileRank} alt="Percentile Rank" className="w-4 h-4" />;
      case "alert":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <TrendingUp className="w-4 h-4" />;
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case "green":
        return "bg-green-50 text-green-600 border-green-200";
      case "red":
        return "bg-red-50 text-red-600 border-red-200";
      case "teal":
        return "bg-[#f3fafd] text-[#07C3CA] border-[#12B6BC]";
      case "yellow":
        return "bg-yellow-50 text-yellow-600 border-yellow-200";
      case "lightblue":
        return "bg-[#f0f5ff] text-[#378ADD] border-[#C7D2FE]";
      default:
        return "bg-[#eef2fc] text-[#133F7D] border-[#133F7D]";
    }
  };

  const getProgressColor = () => {
    switch (color) {
      case "green":
        return "bg-[#185FA5]";
      case "red":
        return "bg-red-600";
      case "teal":
        return "bg-[#07C3CA]";
      case "yellow":
        return "bg-yellow-500";
      case "lightblue":
        return "bg-[#378ADD]";
      default:
        return "bg-[#133F7D]";
    }
  };

  const getValueColor = () => {
    switch (color) {
      case "green":
        return "text-[#185FA5]";
      case "red":
        return "text-[#991B1B]";
      case "teal":
        return "text-[#07C3CA]";
      case "yellow":
        return "text-yellow-600";
      case "lightblue":
        return "text-[#378ADD]";
      default:
        return "text-[#133F7D]";
    }
  };

  const getBadgeSurfaceClasses = () => {
    switch (color) {
      case "green":
        return "bg-[#185FA5]/10";
      case "red":
        return "bg-red-500/10";
      case "teal":
        return "bg-[#07C3CA]/10";
      case "yellow":
        return "bg-yellow-500/10";
      case "lightblue":
        return "bg-[#378ADD]/10";
      default:
        return "bg-[#133F7D]/10";
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div
        className={`h-[2px] w-[88%] rounded-full m-auto shrink-0 ${getProgressColor()}`}
        aria-hidden
      />
      <div className="p-4 md:p-3">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className={`p-1 md:p-2 rounded-lg border ${getColorClasses()}`}>{getIcon()}</div>
            <div className={`text-sm font-semibold ml-2`}>{title}</div>
          </div>

          <div className="text-right">
            <div
              className={`text-xs md:text-xs font-semibold ${getValueColor()} px-2 py-0.5 rounded-md ${getBadgeSurfaceClasses()}`}
            >
              {rightSideValue}
            </div>
          </div>
        </div>
        <div className={`text-2xl md:text-3xl font-bold ${getValueColor()}`}>{value}</div>
        <h3 className="text-[10px] sm:text-xs md:text-xs font-medium text-[#37415180] mb-2">
          {subtitle}
        </h3>
        <div className="w-full bg-gray-200 rounded-full h-1 md:h-1">
          <div
            className={`h-1 md:h-1 rounded-full ${getProgressColor()}`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default ScoreCard;
